import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  Loader2,
  Paintbrush,
  Play,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getAllPublicGenies } from "@/data/discovery/genie-catalog";
import { getBrandForSubcategory } from "@/data/genies/brands";
import { getBrandForPersona } from "@/data/genies/persona-brands";
import {
  useBulkProvision,
  useBulkProvisionIndustry,
  useDemoGenieList,
  useDemoGenieStats,
  useProvisionSingleGenie,
  useReEmbedBranding,
  useRetireDemoGenie,
} from "@/hooks/admin/useDemoGenieAdmin";
import { Agent } from "@/types/agent/agent";
import { DemoGenieRowExpanded } from "./DemoGenieRowExpanded";

const INDUSTRIES = [
  { id: "automotive", name: "Automotive" },
  { id: "marine", name: "Marine" },
  { id: "appliances", name: "Appliances" },
  { id: "office-equipment", name: "Office Equipment" },
  { id: "home-builders", name: "Home Builders" },
  { id: "travel-hospitality", name: "Travel & Hospitality" },
  { id: "manufacturing", name: "Manufacturing" },
  { id: "industrial", name: "Industrial" },
  { id: "trades", name: "Trades" },
];

const DemoGeniesPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [industryFilter, setIndustryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedPersonaId, setExpandedPersonaId] = useState<string | null>(null);
  const { data: demoAgents = [], isLoading } = useDemoGenieList();
  const { data: stats } = useDemoGenieStats();
  const bulkProvision = useBulkProvision();
  const bulkProvisionIndustry = useBulkProvisionIndustry();
  const provisionSingle = useProvisionSingleGenie();
  const retireGenie = useRetireDemoGenie();
  const reEmbedBranding = useReEmbedBranding();

  const allPersonas = getAllPublicGenies();

  const mergedList = allPersonas.map((persona) => {
    const demoAgent = demoAgents.find((a: Agent) => (a as any).persona_id === persona.id);
    return { persona, demoAgent };
  });

  const getStatus = (agent: Agent | undefined): string => {
    if (!agent) return "not_provisioned";
    if (agent.elevenlabs_id) return "ready";
    return "provisioning";
  };

  const filteredList = mergedList.filter(({ persona, demoAgent }) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !persona.name.toLowerCase().includes(q) &&
        !persona.role.toLowerCase().includes(q) &&
        !persona.subcategoryId.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    if (industryFilter !== "all" && persona.industryId !== industryFilter) return false;
    if (statusFilter !== "all") {
      const status = getStatus(demoAgent);
      if (status !== statusFilter) return false;
    }
    return true;
  });

  const handleProvisionAll = () => {
    const unprovisioned = allPersonas
      .filter(
        (p) => !demoAgents.find((a: Agent) => (a as any).persona_id === p.id && a.elevenlabs_id),
      )
      .map((p) => p.id);

    if (unprovisioned.length === 0) {
      toast.info("All genies are already provisioned");
      return;
    }

    bulkProvision.mutate(unprovisioned);
  };

  const handleProvisionIndustry = (industryId: string) => {
    bulkProvisionIndustry.mutate(industryId);
  };

  const handleRowClick = (personaId: string) => {
    setExpandedPersonaId(expandedPersonaId === personaId ? null : personaId);
  };

  const handleOpenDetails = (demoAgent: Agent) => {
    navigate(`/genie-hub/my-genies/${demoAgent.id}`, { state: { genie: demoAgent } });
  };

  const getStatusBadge = (agent: Agent | undefined) => {
    const status = getStatus(agent);
    switch (status) {
      case "ready":
        return (
          <Badge className="bg-genie-primary-light text-genie-primary border-genie-primary/20 inline-flex items-center gap-1 border w-fit">
            <CheckCircle className="w-3 h-3" /> Ready
          </Badge>
        );
      case "provisioning":
        return (
          <Badge className="bg-genie-accent-light text-genie-accent-dark border-genie-accent/20 inline-flex items-center gap-1 border w-fit">
            <Loader2 className="w-3 h-3 animate-spin" /> Provisioning
          </Badge>
        );
      default:
        return (
          <Badge className="bg-genie-background-muted text-genie-text-muted border-genie-border inline-flex items-center gap-1 border w-fit">
            <Clock className="w-3 h-3" /> Not Provisioned
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Sparkles className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Demo Genies</h1>
              <p className="text-genie-text-secondary">
                Manage live demo agents for the {stats?.total || allPersonas.length} genie personas
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select onValueChange={handleProvisionIndustry}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Provision by industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((ind) => (
                  <SelectItem key={ind.id} value={ind.id}>
                    {ind.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => reEmbedBranding.mutate()}
              disabled={reEmbedBranding.isPending}
              variant="outline"
              className="gap-2"
            >
              {reEmbedBranding.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Paintbrush className="w-4 h-4" />
              )}
              Re-embed Branding
            </Button>
            <Button
              onClick={handleProvisionAll}
              disabled={bulkProvision.isPending}
              className="bg-genie-primary hover:bg-genie-primary-hover text-white gap-2"
            >
              {bulkProvision.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Provision All
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Total Personas</p>
                  <p className="text-2xl font-bold text-genie-text-primary tabular-nums">
                    {stats.total}
                  </p>
                </div>
                <Sparkles className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Ready</p>
                  <p className="text-2xl font-bold text-genie-primary tabular-nums">
                    {stats.ready}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Pending</p>
                  <p className="text-2xl font-bold text-genie-accent-dark tabular-nums">
                    {stats.pending}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-genie-accent-dark" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search personas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Industries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {INDUSTRIES.map((ind) => (
                  <SelectItem key={ind.id} value={ind.id}>
                    {ind.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="not_provisioned">Not Provisioned</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="provisioning">Provisioning</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setIndustryFilter("all");
                setStatusFilter("all");
              }}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>


        {/* Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Persona</TableHead>
                <TableHead className="hidden lg:table-cell">Brand</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead className="hidden md:table-cell">Subcategory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-genie-text-secondary">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Loading demo genies...
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-genie-text-muted">
                    No personas match your filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredList.map(({ persona, demoAgent }) => {
                  const isProvisioned = !!demoAgent?.elevenlabs_id;
                  const isExpanded = expandedPersonaId === persona.id;
                  const brand =
                    getBrandForPersona(persona.id) || getBrandForSubcategory(persona.subcategoryId);
                  return (
                    <React.Fragment key={persona.id}>
                      <TableRow
                        className={`cursor-pointer hover:bg-genie-background-hover ${isExpanded ? "border-b-0" : ""}`}
                        onClick={() => handleRowClick(persona.id)}
                      >
                        <TableCell className="w-10">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-sm text-genie-text-primary">
                              {persona.name}
                            </div>
                            <div className="text-xs text-genie-text-secondary">{persona.role}</div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {brand ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <div
                                  className="w-3 h-3 rounded-full border border-gray-200"
                                  style={{ backgroundColor: brand.primaryColor }}
                                  title={brand.primaryColor}
                                />
                                <div
                                  className="w-3 h-3 rounded-full border border-gray-200"
                                  style={{ backgroundColor: brand.secondaryColor }}
                                  title={brand.secondaryColor}
                                />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-genie-text-primary">
                                  {brand.brandName}
                                </div>
                                <div className="text-xs text-genie-text-muted">{brand.tagline}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-genie-text-muted">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-genie-text-primary capitalize">
                          {persona.industryId}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-genie-text-secondary">
                          {persona.subcategoryId}
                        </TableCell>
                        <TableCell>{getStatusBadge(demoAgent)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            {!demoAgent && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => provisionSingle.mutate(persona.id)}
                                disabled={provisionSingle.isPending}
                                className="text-xs gap-1"
                              >
                                {provisionSingle.isPending ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Play className="w-3 h-3" />
                                )}
                                Provision
                              </Button>
                            )}
                            {demoAgent?.elevenlabs_id && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenDetails(demoAgent)}
                                  className="text-xs gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Details
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => retireGenie.mutate(demoAgent.id)}
                                  className="text-xs text-genie-error hover:text-genie-error-hover hover:bg-genie-error-light gap-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Retire
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <DemoGenieRowExpanded persona={persona} demoAgent={demoAgent} />
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-genie-border">
            <div className="text-sm text-genie-text-secondary">
              Showing {filteredList.length} of {allPersonas.length} personas
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DemoGeniesPage;
